import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { addMonths, eachDayOfInterval, endOfMonth, format, getDay, isBefore, isSameDay, parseISO, startOfMonth } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { TODAY } from '../../utils/format';
import { spring } from '../../utils/motion';
interface AvailabilityCalendarProps {
  bookedDates: string[];
}
export function AvailabilityCalendar({
  bookedDates
}: AvailabilityCalendarProps) {
  const [offset, setOffset] = useState(0);
  const [blocked, setBlocked] = useState<string[]>(['2026-10-15', '2026-10-16']);
  const month = addMonths(startOfMonth(TODAY), offset);
  const days = eachDayOfInterval({
    start: month,
    end: endOfMonth(month)
  });
  const lead = (getDay(month) + 6) % 7;
  return <div data-id="element-557" data-mp="3k31hjbza1pen">
      <div className="flex items-center justify-between" data-id="element-558" data-mp="3k0p2b6kwlt5a">
        <p className="font-bold" data-id="element-559" data-mp="3f4q8h3gt3ixh">{format(month, 'MMMM yyyy')}</p>
        <div className="flex gap-1" data-id="element-560" data-mp="1ic0p3y6gv1qz">
          <button type="button" aria-label="Previous month" disabled={offset === 0} onClick={() => setOffset(o => o - 1)} className="grid h-9 w-9 place-items-center rounded-full text-ink-2 transition-colors duration-150 hover:bg-surface-2 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-561" data-mp="1id26slhzmmho">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-562" data-mp="21a3h8lub9et0" />
          </button>
          <button type="button" aria-label="Next month" disabled={offset === 5} onClick={() => setOffset(o => o + 1)} className="grid h-9 w-9 place-items-center rounded-full text-ink-2 transition-colors duration-150 hover:bg-surface-2 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-563" data-mp="269fzsue5fzps">
            <ChevronRightIcon className="h-5 w-5" data-id="element-564" data-mp="2ozo1r9644k4e" />
          </button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-bold text-ink-3" data-id="element-565" data-mp="pv642rkzlmh0">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i} className="py-1" data-id="element-566" data-mp="kui43ges5van" data-mpi={i}>
            {d}
          </span>)}
      </div>
      <div className="grid grid-cols-7 gap-1" data-id="element-567" data-mp="yq3yy47p39az">
        {Array.from({
        length: lead
      }).map((_, i) => <span key={`l${i}`} data-id="element-568" data-mp="tv6i4b46bvnj" data-mpi={i} />)}
        {days.map(d => {
        const iso = format(d, 'yyyy-MM-dd');
        const booked = bookedDates.some(b => isSameDay(parseISO(b), d));
        const isBlocked = blocked.includes(iso);
        const past = isBefore(d, TODAY) && !isSameDay(d, TODAY);
        return <motion.button key={iso} type="button" disabled={past || booked} whileTap={{
          scale: 0.88
        }} transition={spring.snappy} onClick={() => setBlocked(b => b.includes(iso) ? b.filter(x => x !== iso) : [...b, iso])} aria-label={`${format(d, 'd MMMM')}${booked ? ', booked' : isBlocked ? ', unavailable' : ', available'}`} aria-pressed={isBlocked} className={twMerge('tabular grid aspect-square place-items-center rounded-sm text-sm font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', past && 'text-ink-3/50', !past && !booked && !isBlocked && 'hover:bg-surface-2', booked && 'bg-brand text-on-brand', isBlocked && 'bg-surface-2 text-ink-3 line-through', isSameDay(d, TODAY) && !booked && 'ring-2 ring-inset ring-ink')} data-id="element-569" data-mp="1dkqolewsgrse">
              {format(d, 'd')}
            </motion.button>;
      })}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[13px] text-ink-3" data-id="element-570" data-mp="at7dq3dudi33">
        <span className="flex items-center gap-1.5" data-id="element-571" data-mp="fs0t61covj5w">
          <span className="h-3 w-3 rounded-[4px] bg-brand" data-id="element-572" data-mp="fpem02tpn5o7" /> Booked
        </span>
        <span className="flex items-center gap-1.5" data-id="element-573" data-mp="2bu4590br2qse">
          <span className="h-3 w-3 rounded-[4px] bg-surface-2 ring-1 ring-line" data-id="element-574" data-mp="26vap1052wloj" /> Unavailable (tap to toggle)
        </span>
      </div>
    </div>;
}